// src/design/DesignCenterWelcome.jsx
// Welcome modal for Design Center - shows once per user, requires scroll + acknowledgment

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useProfileStore } from '../state/profileStore';

export default function DesignCenterWelcome({ onAcknowledge }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const updateProfile = useProfileStore((s) => s.updateProfile);

  // Check if user has scrolled to the bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Consider "at bottom" if within 10px of the end
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    if (atBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  // Check on mount if content doesn't need scrolling
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // If content fits without scrolling, enable checkbox immediately
    if (el.scrollHeight <= el.clientHeight) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleCheckboxChange = (e) => {
    if (hasScrolledToBottom) {
      setIsChecked(e.target.checked);
    }
  };

  const handleAcknowledge = async () => {
    if (!isChecked || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const _token = localStorage.getItem('token');
      const response = await fetch(
        `/api/users/acknowledge-design-center`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            ...(_token && { Authorization: `Bearer ${_token}` }),
          },
        }
      );

      if (response.ok) {
        // Update local profile state
        updateProfile({ u_design_center_acknowledged: true });
        onAcknowledge();
      } else {
        console.error('Failed to acknowledge Design Center');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error acknowledging Design Center:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/10">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="bg-[#00aef7] text-white px-6 py-1 shrink-0">
          <h2 className="text-l font-bold">Welcome to the eHandshake Design Center</h2>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-2 text-gray-700 leading-relaxed scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent overscroll-contain"
        >
          <p className="mb-2 text-sm">
            eHandshake is all about small, local businesses and the communities they serve. We are a business to business, collaboration platform that allows businesses to form local teams and promote each other's content into their respective social & business channels.
          </p>

          <p className="mb-2 text-sm">
            So, it's all about your business or industry and how they relate to your local community. 
          </p>

          <p className="mb-4 text-sm">
            Political posts, religious posts, adult content, or any other inappropriate material are not allowed on the eHandshake site. Remember that your teammates will be posting your creations into their channels. The goal here is to create informative, educational and engaging posts that relate to your business and the relevant industry you serve. It's only about business. Keep it clean, positive and uplifting!
          </p>

          <p className="mb-2 text-sm bg-amber-50 border border-amber-200 rounded-sm p-3 text-amber-800">
            eHandshake reserves the right to remove any content that is deemed inappropriate or not in line with our community guidelines. Repeated violations may result in suspension or termination of your account.
          </p>

          <p className="mb-2 text-sm text-[#00aef7]">
            Enlighten, engage, teach, inform... make something your teammates will be proud to post into their own social and business channels.
          </p>
        </div>

        {/* Footer with Checkbox */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 shrink-0">
          {!hasScrolledToBottom && (
            <p className="text-sm text-[#00aef7] mb-3 text-center italic">
              Please scroll down to read the full message
            </p>
          )}

          <label
            className={`flex items-center gap-3 cursor-pointer ${
              !hasScrolledToBottom ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              disabled={!hasScrolledToBottom || isSubmitting}
              className="w-5 h-5 rounded border-gray-300 text-[#00aef7] focus:ring-[#00aef7] disabled:opacity-50"
            />
            <span className="text-sm font-medium text-gray-700">
              I understand and agree to these guidelines
            </span>
          </label>

          <button
            onClick={handleAcknowledge}
            disabled={!isChecked || isSubmitting}
            className={`mt-4 w-full py-2.5 rounded-lg font-semibold transition-colors ${
              isChecked && !isSubmitting
                ? 'bg-[#00aef7] text-white hover:bg-[#0095d6]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Continue to Design Center'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
